import React, { useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Form,
  Button,
  Dropdown,
  Grid,
  GridRow,
  GridColumn,
  Gutter,
  DateUtil,
  FormLabel,
  AsyncConnector,
} from '@axiom/ui';
import type {
  AreaOfStudy,
  Candidate,
  Education,
  EducationEdit,
  LawSchoolSearch,
} from '@axiom/validation';
import { EducationEditSchema } from '@axiom/validation';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateDegreesApi } from '../../api/candidate-degrees-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { EducationApi } from '../../api/education-api';
import { EducationUtil } from '../../utils/education-util';
import { LawSchoolApi } from '../../api/law-school-api';

import { EducationBannerPrompt } from './EducationBannerPrompt';

export type EducationEditModalProps = {
  educations: Education[];
  candidateId: Candidate['id'];
  modifyEducation: Education | Record<PropertyKey, never>;
  areasOfStudy: AreaOfStudy[];
  onClose: () => void;
};

export const EducationEditModal = ({
  educations,
  candidateId,
  modifyEducation,
  areasOfStudy,
  onClose,
}: EducationEditModalProps) => {
  const {
    id: educationId,
    lawSchoolId,
    areaOfStudyId,
    yearAwarded,
    degree,
  } = modifyEducation;
  const lawSchoolConnector = useMemo(
    () =>
      new AsyncConnector<LawSchoolSearch>({
        getData: args => {
          return LawSchoolApi.readLawSchools(args.search, args.page);
        },
        parseData: response => {
          return {
            currentPage: response.meta.currentPage,
            totalPages: response.meta.pageCount,
            resultCount: response.meta.resultCount,
            data: response.data,
          };
        },
      }),
    [lawSchoolId]
  );

  return (
    <Form
      name="EDUCATION_EDIT_FORM"
      schema={EducationEditSchema}
      initialValues={
        {
          candidateId,
          lawSchoolId,
          areaOfStudyId,
          yearAwarded,
          degree,
        } as EducationEdit
      }
      onSubmit={async (changedValues: Partial<EducationEdit>) => {
        await (educationId
          ? EducationApi.updateEducation(educationId, changedValues)
          : CandidateDegreesApi.createEducation(candidateId, {
              ...(changedValues as EducationEdit),
              candidateId,
            }));

        await CandidateApi.refreshCandidate(candidateId);
        await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
          candidateId
        );
        onClose();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="EDUCATION_EDIT_MODAL">
            <ModalHeader
              name="EDUCATION_EDIT_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Edit Education
            </ModalHeader>
            <ModalSection disableScrolling>
              {educations.length === 0 && (
                <Gutter bottom="16px">
                  <EducationBannerPrompt />
                </Gutter>
              )}
              <Grid>
                <GridRow>
                  <GridColumn>
                    <Dropdown
                      description="If your institution does not appear, reach out to your talent partner and they can make it available."
                      displayKey="name"
                      label="Institution"
                      name="lawSchoolId"
                      options={lawSchoolConnector}
                      valueKey="id"
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                    <Gutter top="16px" />
                    <Gutter bottom="8px">
                      <FormLabel name="PENDO-CHOOSE-AREA-OF-STUDY">
                        Area of study
                      </FormLabel>
                    </Gutter>
                    <Dropdown
                      displayKey="label"
                      name="areaOfStudyId"
                      options={EducationUtil.getAreasOfStudyOptions(
                        areasOfStudy
                      )}
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                    <Gutter top="16px" />
                    <Dropdown
                      displayKey="label"
                      label="Year"
                      name="yearAwarded"
                      options={DateUtil.getYearOptionsByRange(
                        new Date().getFullYear() + 10,
                        1950
                      )}
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                    <Gutter top="16px" />
                    <Dropdown
                      displayKey="label"
                      label="Degree"
                      name="degree"
                      options={EducationUtil.getDegreeOptions(
                        educations,
                        modifyEducation
                      )}
                      valueKey="value"
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                onClick={() => {
                  onClose();
                }}
                name="CANCEL"
              >
                Cancel
              </Button>
              <Button type="submit" name="SAVE" onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
